import { useCallback, useEffect, useState } from "react";

import {
  createFlow as apiCreateFlow,
  deleteFlow as apiDeleteFlow,
  listFlows,
  toggleFlow as apiToggleFlow,
  updateFlow as apiUpdateFlow,
} from "@/api/flows";
import { Flow, FlowCreate } from "@/types";

export function useFlows() {
  const [flows, setFlows] = useState<Flow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFlows = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listFlows({ limit: 500, offset: 0 });
      setFlows(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load flows");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlows();
  }, [fetchFlows]);

  const createFlow = useCallback(
    async (flow: FlowCreate): Promise<Flow | null> => {
      try {
        const created = await apiCreateFlow(flow);
        setFlows((prev) => [created, ...prev]);
        return created;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to create flow");
        return null;
      }
    },
    [],
  );

  const updateFlow = useCallback(
    async (id: string, flow: FlowCreate): Promise<Flow | null> => {
      try {
        const updated = await apiUpdateFlow(id, flow);
        setFlows((prev) => prev.map((f) => (f.id === id ? updated : f)));
        return updated;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to update flow");
        return null;
      }
    },
    [],
  );

  const deleteFlow = useCallback(async (id: string): Promise<boolean> => {
    try {
      await apiDeleteFlow(id);
      setFlows((prev) => prev.filter((f) => f.id !== id));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete flow");
      return false;
    }
  }, []);

  const toggleFlow = useCallback(async (id: string): Promise<boolean> => {
    try {
      const updated = await apiToggleFlow(id);
      setFlows((prev) => prev.map((f) => (f.id === id ? updated : f)));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to toggle flow");
      return false;
    }
  }, []);

  return {
    flows,
    isLoading,
    error,
    refresh: fetchFlows,
    createFlow,
    updateFlow,
    deleteFlow,
    toggleFlow,
  };
}
